import {
  ComponentProps,
  CSSProperties,
  useCallback,
  useEffect,
  useState,
} from 'react';

import filterInvalidDOMPropsModule from 'filter-invalid-dom-props';
import { useDropzone } from 'react-dropzone';

import cx from 'classnames';

import { ImageBackground } from './ImageBackground/ImageBackground';
import styles from './InputImageUploader.module.scss';
import { FileDto } from '@skillarea/lib-shared';
import { useIsDragStarted } from '../../../hooks';
import { TInputImageUploaderTexts } from '../../../types';
import { IUploadStore } from '../../../stores/uploadStore';
import { useFileLoader } from './useFileUploader';
import { useToaster } from '../../toast';
import { Icon, Spinner, TextBlock } from '../../common';
import { ButtonText } from '../../buttons';
import { StoreApi } from 'zustand';
import { AvatarCropDialog } from './avatar-crop-dialog';

type TImageUploaderProps<C> = {
  accept?: Record<string, string[]>;
  className?: string;
  isMobile?: boolean;
  maxSize?: number;
  onChange?: (value: FileDto) => void;
  readonly?: boolean;
  size?: 's' | 'm';
  slot: string;
  store: StoreApi<IUploadStore<C>>;
  texts: TInputImageUploaderTexts;
  classes?: Partial<Record<'image' | 'screen' | 'name', string>>;
  type?: 'image' | 'file';
  value?: { name: string; url: string };
} & Omit<ComponentProps<'div'>, 'onChange'>;

type TFilterInvalidDOMProps = (props: unknown) => Record<string, unknown>;

function isFilterInvalidDOMPropsFunction(
  value: unknown,
): value is TFilterInvalidDOMProps {
  return typeof value === 'function';
}

function getFilterInvalidDOMProps(
  moduleValue: unknown,
): TFilterInvalidDOMProps {
  if (isFilterInvalidDOMPropsFunction(moduleValue)) {
    return moduleValue;
  }

  if (
    typeof moduleValue === 'object' &&
    moduleValue !== null &&
    'default' in moduleValue
  ) {
    const nestedDefault = (moduleValue as Record<string, unknown>).default;

    if (isFilterInvalidDOMPropsFunction(nestedDefault)) {
      return nestedDefault;
    }
  }

  throw new Error('filter-invalid-dom-props export is not callable');
}

const safeFilterInvalidDOMProps = getFilterInvalidDOMProps(
  filterInvalidDOMPropsModule as unknown,
);

type TCropDialogState = {
  file: File;
  imageSrc: string | null;
};

function shouldForwardOnChange(slot: string, value: FileDto): boolean {
  if (slot !== 'avatar') {
    return true;
  }

  return Boolean(value.url) || value.name === '';
}

function readFileAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = () => {
      if (typeof reader.result === 'string') {
        resolve(reader.result);
        return;
      }

      reject(new Error('Failed to read image'));
    };

    reader.onerror = () => {
      reject(reader.error ?? new Error('Failed to read image'));
    };

    reader.readAsDataURL(file);
  });
}

export const InputImageUploader = <Context = unknown,>({
  accept,
  className,
  isMobile,
  maxSize,
  onChange,
  readonly,
  size = 'm',
  slot,
  store,
  texts,
  type = 'image',
  value,
  classes,
  ...rest
}: TImageUploaderProps<Context>) => {
  const handleFileLoaderChange = useCallback(
    (nextValue: FileDto) => {
      if (!shouldForwardOnChange(slot, nextValue)) {
        return;
      }

      onChange?.(nextValue);
    },
    [onChange, slot],
  );

  const { clearFile, getFullUrl, isLoading, onDrop } = useFileLoader(
    store,
    slot,
    handleFileLoaderChange,
  );
  const { toast } = useToaster();

  const isDragStarted = useIsDragStarted();

  const [uploadingImage, setUploadingImage] = useState<
    string | ArrayBuffer | null
  >('');

  const [cropDialogState, setCropDialogState] =
    useState<TCropDialogState | null>(null);

  const startUpload = useCallback(
    (file: File) => {
      void readFileAsDataUrl(file).then(
        (imageSrc) => setUploadingImage(imageSrc),
        () => setUploadingImage(''),
      );
      void onDrop([file]);
    },
    [onDrop],
  );

  const openAvatarCropDialog = useCallback(
    (file: File) => {
      setCropDialogState({ file, imageSrc: null });

      void readFileAsDataUrl(file).then(
        (imageSrc) => {
          setCropDialogState((currentState) => {
            if (!currentState || currentState.file !== file) {
              return currentState;
            }

            return {
              ...currentState,
              imageSrc,
            };
          });
        },
        () => {
          setCropDialogState((currentState) => {
            if (!currentState || currentState.file !== file) {
              return currentState;
            }

            return null;
          });
          toast.error(texts.wrongFileFormat);
        },
      );
    },
    [texts.wrongFileFormat, toast],
  );

  const handleDrop = useCallback(
    (acceptedFiles: File[]) => {
      const file = acceptedFiles[0];

      if (file) {
        if (slot === 'avatar') {
          openAvatarCropDialog(file);
        } else {
          startUpload(file);
        }
      }
    },
    [openAvatarCropDialog, slot, startUpload],
  );

  const handleCropConfirm = useCallback(
    (croppedFile: File) => {
      setCropDialogState(null);
      startUpload(croppedFile);
    },
    [startUpload],
  );

  const handleCropCancel = useCallback(() => {
    setCropDialogState(null);
  }, []);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    if (!isLoading) setUploadingImage('');
  }, [isLoading]);

  const { fileRejections, getInputProps, getRootProps, isDragActive } =
    useDropzone({
      onDrop: handleDrop,
      multiple: false,
      maxSize: maxSize || 5 * 1024 * 1024,
      accept: accept || {
        'image/jpeg': ['.jpeg', '.jpg'],
        'image/png': ['.png'],
      },
    });

  useEffect(() => {
    if (fileRejections.length) {
      if (fileRejections[0].errors[0].code === 'file-too-large') {
        toast.error(texts.tooLargeSize);
      } else {
        toast.error(texts.wrongFileFormat);
      }
    }
  }, [fileRejections, texts, toast]);

  const inputProps = getInputProps();
  const Dropzone = (
    <div className={styles.dropzone} {...getRootProps()}>
      <input {...inputProps} data-testid={`file-input-${slot}`} />
    </div>
  );

  return (
    <div
      className={cx(styles.imageUploader, className)}
      {...safeFilterInvalidDOMProps(rest)}
    >
      <div
        className={cx(
          styles.content,
          styles[size],
          isDragActive && styles.drag,
          isMobile && styles.mobile,
          readonly && styles.readonly,
        )}
      >
        {!value?.url && (
          <ImageBackground
            accept={
              accept || {
                'image/jpeg': ['.jpeg', '.jpg'],
                'image/png': ['.png'],
              }
            }
            isDragging={!readonly && (isDragStarted || isDragActive)}
            isLoading={isLoading}
            isMobile={isMobile}
            maxSize={maxSize || 5 * 1024 * 1024}
            size={size}
            texts={texts.background}
          />
        )}

        {type === 'image' ? (
          <Image
            className={classes?.image}
            url={isLoading ? uploadingImage || '' : getFullUrl(value)}
          />
        ) : (
          value?.url && <Icon color="contrast" img="file" size={60} />
        )}

        {isLoading && <ImageSpinner />}

        {!isLoading && value?.url && (
          <ImageLink className={classes?.screen} href={getFullUrl(value)} />
        )}

        {!isLoading && !readonly && Dropzone}

        {(value?.name || value?.url) && (
          <>
            <TextBlock
              className={cx(classes?.name, styles.fileName)}
              color="contrast"
              size="xs"
            >
              {value?.name || texts.noname}
            </TextBlock>

            {!readonly && (
              <ButtonText
                className={styles.delete}
                color="note"
                iconStart="close"
                onClick={clearFile}
                size="m"
              />
            )}
          </>
        )}
      </div>

      <AvatarCropDialog
        fileName={cropDialogState?.file.name ?? 'avatar.png'}
        fileType={cropDialogState?.file.type ?? 'image/png'}
        imageSrc={cropDialogState?.imageSrc ?? null}
        isOpen={Boolean(cropDialogState)}
        texts={texts.crop}
        onClose={handleCropCancel}
        onConfirm={handleCropConfirm}
      />
    </div>
  );
};

const Image = ({
  className,
  url,
}: {
  className?: string;
  url: string | ArrayBuffer;
}) => {
  const imageStyle = {
    '--url': `url("${url}")`,
  } as CSSProperties & Record<'--url', string>;

  return <div className={cx(className, styles.image)} style={imageStyle} />;
};

const ImageSpinner = () => (
  <>
    <div className={styles.screen} />
    <Spinner className={styles.spinner} />
  </>
);

// todo: probably refactor to use a modal
const ImageLink = ({
  className,
  href,
}: {
  className?: string;
  href: string;
}) => (
  <a
    aria-label="View full image"
    className={cx(className, styles.screen)}
    href={href}
    rel="noreferrer"
    target="_blank"
  >
    <span style={{ display: 'none' }}>View full image</span>
  </a>
);
